import React, { ChangeEvent } from 'react'

import { SelectProps } from '@at/design-system-elements'
import { Option } from '../../utils/search/search-results-util/search-results.util'
import { useFilter } from '../../forms/hocs/filter.context.hoc'
import { ScreenReaderOnly } from '../screen-reader-only/screen-reader-only'
import { ValueChangedProp } from '../../forms/hocs/types'
import { asFormattedString } from '@at/formatter-utils/lib/number-utils/number.utils'
import { StyledSelect } from './search-filters-dropdown.styles'
import { FilterName, SearchResultsSort } from '../../__generated__/fed-graphql-types'
import { useForm } from '../../forms/hocs/form.context'

const searchFiltersDropdownClassName = 'search-filters-dropdown'

const handleChangeSingleSelect = (
    onValueChanged: ValueChangedProp['onValueChanged'],
    shouldResetSort: boolean,
    updateSortBySelected: (sort: string | null, shouldResetSort?: boolean) => void
) => {
    return (e: ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value
        if (shouldResetSort && value === 'new') {
            updateSortBySelected(null, false)
        }
        onValueChanged!(value ? [value] : [])
    }
}

const handleChangeMultipleSelect = (onValueChanged: ValueChangedProp['onValueChanged']) => {
    return (e: ChangeEvent<HTMLSelectElement>) => {
        const options = [...e.target.selectedOptions]
        const values = options.map((option) => option.value)

        if (values.includes('')) {
            // "Any" selected, clear all selected
            onValueChanged!([])
        } else {
            onValueChanged!(values)
        }
    }
}

export const SearchFiltersDropdown = ({ multiSelect = false, selectClassName = searchFiltersDropdownClassName }) => {
    const {
        filterName,
        title,
        subTitle,
        ariaLabel,
        options,
        placeholder,
        hideLabel = false,
        selected,
        isDisabled,
        onValueChanged = () => {},
    } = useFilter()

    const { updateSortBySelected, sortBySelected } = useForm()

    const shouldResetSort =
        filterName === FilterName.min_year_manufactured &&
        (sortBySelected === SearchResultsSort.mileage ||
            sortBySelected === SearchResultsSort.year_asc ||
            sortBySelected === SearchResultsSort.year_dsc)

    const optionsFormatted = options.slice().map(({ label, value, count }: Option) => ({
        displayValue: label + (count != null ? ` (${asFormattedString(count)})` : ''),
        value: value as string | number,
        selected: undefined,
    }))

    if (placeholder) {
        optionsFormatted.unshift({ displayValue: placeholder, value: '', selected: undefined })
    }

    const selectProps: SelectProps = {
        id: filterName as string,
        name: filterName as string,
        label: title,
        helpText: subTitle,
        options: optionsFormatted,
        hideLabel,
        value: multiSelect ? selected : selected[0] ?? '',
        onChange: multiSelect
            ? handleChangeMultipleSelect(onValueChanged)
            : handleChangeSingleSelect(onValueChanged, shouldResetSort, updateSortBySelected),
        'aria-label': ariaLabel ? ariaLabel : title,
        disabled: !options.length,
        multiSelect,
    }

    return (
        <div className="search-filters-dropdown-container" data-testid={`${filterName}-container`}>
            <StyledSelect {...selectProps} className={selectClassName} disabled={isDisabled} />
            <ScreenReaderOnly
                id={`${filterName}-error`}
                description={`Select an option for ${ariaLabel ? ariaLabel : title}`}
            />
        </div>
    )
}
